import { useIntervalEffect } from "@react-hookz/web";
import classNames from "classnames";
import { geoOrthographic, geoPath } from "d3-geo";
import { create } from "d3-selection";
import { AnimatePresence, m } from "framer-motion";
import { JSX, MouseEvent, PropsWithoutRef, TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useErrorBoundary } from "react-error-boundary";
import { feature } from "topojson-client";
import { Point, Topology } from "topojson-specification";
import sleep from "~/helpers/sleep";
import useMapIncidents from "~/hooks/api/useMapIncidents";
import styles from "./Incidents.module.scss";
import Notification from "./Notification";
import worldRaw from "./land-110m.json";

export type IncidentsProps = Overwrite<Omit<PropsWithoutRef<JSX.IntrinsicElements["div"]>, "children">, {
  enableMapIncidents?: boolean;
}>;

type State = {
  landPathD: string | null;
  manualRotateXReference: number | null;
  mapIncident: HydratedMapIncident | null;
  pinPathD: string | null;
  rotateX: number;
};

const world = (worldRaw as Topology<any>);
const geoJsonFeature = feature(world, world.objects.land);

const width = 960;
const height = 500;

const outerCircleCommonClassNames = "aspect-ratio-1 start-50 top-50 position-absolute rounded-circle translate-middle z-n1";

export default function Incidents({ enableMapIncidents = true, ...props }: IncidentsProps) {
  const { showBoundary } = useErrorBoundary();
  const { data: swrMapIncidents } = useMapIncidents(enableMapIncidents, { refreshInterval: 10000 });
  const [state, setState] = useState<State>({
    landPathD: null,
    manualRotateXReference: null,
    mapIncident: null,
    pinPathD: null,
    rotateX: 0,
  });
  const projection = useMemo(() => geoOrthographic().rotate([state.rotateX, 0]), [state.rotateX]);
  const path = useMemo(() => geoPath().projection(projection), [projection]);
  const mapIncidents = useRef<Array<HydratedMapIncident> | null>(null);
  const mapIncidentIndex = useRef<number>(0);
  const isLoopStarted = useRef<boolean>(false);
  const isReady = useRef<boolean>(false);

  const handleManualRotate = useCallback((e: MouseEvent | TouchEvent) => {
    const { clientX } = isTouchEvent(e) ? e.touches[0] : e;
    // eslint-disable-next-line prefer-destructuring
    const manualRotateXReference = state.manualRotateXReference;

    if (manualRotateXReference !== null) {
      setState((s) => ({ ...s, manualRotateXReference: clientX, rotateX: (s.rotateX + ((clientX - manualRotateXReference) / 3)) }));
    }
  }, [state.manualRotateXReference]);

  const handleManualRotateStart = useCallback((e: MouseEvent | TouchEvent) => {
    const { clientX } = isTouchEvent(e) ? e.touches[0] : e;

    setState((s) => ({ ...s, manualRotateXReference: clientX }));
  }, []);

  const handleManualRotateEnd = useCallback(() => {
    setState((s) => ({ ...s, manualRotateXReference: null }));
  }, []);

  useEffect(() => {
    setState((s) => ({ ...s, landPathD: create("path").datum(geoJsonFeature).attr("d", path).attr("d") }));
  }, [path]);

  useEffect(() => {
    if (state.mapIncident !== null && state.mapIncident.latitude !== null && state.mapIncident.longitude !== null) {
      const point: Point = { coordinates: [state.mapIncident.longitude, state.mapIncident.latitude], type: "Point" };

      setState((s) => ({ ...s, pinPathD: create("path").datum(point).attr("d", path.pointRadius(10)).attr("d") }));
    } else {
      setState((s) => ({ ...s, pinPathD: null }));
    }
  }, [path, projection, state.mapIncident]);

  useEffect(() => {
    mapIncidents.current = swrMapIncidents || null;
  }, [swrMapIncidents]);

  useEffect(() => {
    isReady.current = true;
  }, []);

  useEffect(() => {
    let loop = true;

    async function run() {
      while (loop) {
        if (mapIncidents.current === null || mapIncidents.current.length === 0) {
          await sleep(1000);
          continue;
        }

        const mapIncident: HydratedMapIncident | null =
          mapIncidents.current[mapIncidentIndex.current % mapIncidents.current.length] || null;

        if (mapIncident !== null) {
          setState((s) => ({ ...s, mapIncident }));
          await sleep(5000);
          setState((s) => ({ ...s, mapIncident: null }));
          await sleep(1000);
          mapIncidentIndex.current += 1;
        }
      }
    }

    if (enableMapIncidents && !isLoopStarted.current) {
      isLoopStarted.current = true;
      run().catch((e) => showBoundary(e));
    }

    return () => {
      isLoopStarted.current = false;
      loop = false;
    };
  }, [enableMapIncidents, showBoundary]);

  useIntervalEffect(() => {
    setState((s) => ({ ...s, rotateX: s.rotateX + 0.4 }));
  }, state.manualRotateXReference === null ? 40 : undefined);

  return (
    <div {...props}>
      <OverlayTrigger
        overlay={enableMapIncidents ? (
          <Tooltip className="z-2">
            This shows the incidents caught by
            {" "}
            <span className="fw-medium">WebGazer</span>
            {" "}
            in real-time!
          </Tooltip>
        ) : <></>}
        placement="top"
      >
        <div className="aspect-ratio-1 position-relative">
          <div className={classNames(outerCircleCommonClassNames, styles.outerCircle)} />
          <div className={classNames(outerCircleCommonClassNames, styles.outerCircle2)} />
          <div className={classNames(outerCircleCommonClassNames, styles.outerCircle3)} />

          <AnimatePresence>
            {state.mapIncident !== null ? (
              <Notification
                animate={{ opacity: 1, translateX: "-50%", translateY: 0 }}
                className="bottom-0 mw-100 position-absolute start-50 z-3"
                exit={{ opacity: 0, translateX: "-50%", translateY: "20px" }}
                initial={{ opacity: 0, translateX: "-50%", translateY: "20px" }}
                key={state.mapIncident.id}
                mapIncident={state.mapIncident}
                transition={{ duration: 0.5 }}
              />
            ) : null}
          </AnimatePresence>

          <div className="aspect-ratio-1 overflow-hidden position-relative">
            <svg
              className="d-block overflow-visible position-absolute"
              style={{ height: "90%", left: "5%", top: "5%", transform: "translateX(-24%)" }}
              viewBox={`0 0 ${width} ${height}`}
            >
              <defs>
                <filter id="incidents-world-outer-shadow">
                  <feDropShadow className="text-primary" dx={0} dy={0} floodColor="currentColor" stdDeviation={4} />
                </filter>

                <linearGradient
                  gradientTransform="rotate(15)"
                  id="incidents-world-sea-gradient"
                  x1="0%"
                  x2="100%"
                  y1="0%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#42A9C3" stopOpacity="1" />
                  <stop offset="50%" stopColor="#3198C2" stopOpacity="1" />
                </linearGradient>

                <mask id="incidents-world-limit">
                  <circle cx={width / 2} cy={height / 2} fill="white" r={projection.scale()} />
                </mask>

                <mask id="incidents-world-arc-shadow">
                  <circle cx={width / 2} cy={height / 2} fill="white" r={projection.scale()} />
                  <circle cx={width * 0.58} cy={height * 0.58} fill="black" r={projection.scale()} />
                </mask>

                <radialGradient id="incidents-world-radial-gradient">
                  <stop offset="75%" stopColor="#117BBF" stopOpacity={0} />
                  <stop offset="100%" stopColor="#117BBF" stopOpacity={1} />
                </radialGradient>
              </defs>

              {/* outer shadow */}
              <circle
                cx={width / 2}
                cy={height / 2}
                r={projection.scale()}
                style={{ filter: "url(#incidents-world-outer-shadow)" }}
              />

              {/* sea */}
              <circle cx={width / 2} cy={height / 2} fill="url(#incidents-world-sea-gradient)" r={projection.scale()} />

              {state.landPathD !== null ? (
                <path
                  className="text-white"
                  d={state.landPathD}
                  style={{
                    fill: "#F9F9F9",
                    stroke: "currentColor",
                  }}
                />
              ) : null}

              {/* pin */}
              <AnimatePresence>
                {state.pinPathD !== null ? (
                  <m.path
                    animate={{ opacity: 1 }}
                    className="text-warning"
                    d={state.pinPathD}
                    exit={{ opacity: 0 }}
                    fill="currentColor"
                    initial={{ opacity: 0 }}
                    mask="url(#incidents-world-limit)"
                    transition={{ duration: 0.5 }}
                  />
                ) : null}
              </AnimatePresence>

              {/* 3d gradient fill */}
              <circle
                cx={width / 2}
                cy={height / 2}
                fill="url(#incidents-world-radial-gradient)"
                r={projection.scale()}
              />

              {/* arc shadow on top left */}
              <circle
                cx={width / 2}
                cy={height / 2}
                fill="currentColor"
                mask="url(#incidents-world-arc-shadow)"
                opacity={0.25}
                r={projection.scale()}
              />
            </svg>
          </div>
          <button
            className={classNames(
              "btn-unstyled inset-0 position-absolute rounded-circle z-2",
              state.manualRotateXReference === null ? "cursor-grab" : "cursor-grabbing",
            )}
            onMouseDown={handleManualRotateStart}
            onMouseLeave={handleManualRotateEnd}
            onMouseMove={handleManualRotate}
            onMouseUp={handleManualRotateEnd}
            onTouchEnd={handleManualRotateEnd}
            onTouchMove={handleManualRotate}
            onTouchStart={handleManualRotateStart}
          />
        </div>
      </OverlayTrigger>
    </div>
  );
}

function isTouchEvent(e: MouseEvent | TouchEvent): e is TouchEvent {
  return "touches" in e;
}
